import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { TLocalCartState, TLocalCartItem } from './localCart.types';
import {
  loadCartFromStorage,
  saveCartToStorage,
  clearCartFromStorage,
  cartItemsEqual,
} from './localCart.utils';

const initialState: TLocalCartState = {
  items: loadCartFromStorage(),
};

const localCartSlice = createSlice({
  name: 'localCart',
  initialState,
  reducers: {
    addItem: (state, action: PayloadAction<TLocalCartItem>) => {
      const newItem = action.payload;
      const existingItemIndex = state.items.findIndex((item) =>
        cartItemsEqual(item, newItem)
      );

      if (existingItemIndex >= 0) {
        // Item with same id and values exists, increase quantity
        state.items[existingItemIndex].quantity += newItem.quantity;
      } else {
        // New item, add to cart
        state.items.push(newItem);
      }

      saveCartToStorage(state.items);
    },
    removeItemById: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter((item) => item.id !== action.payload);
      saveCartToStorage(state.items);
    },
    updateItemQuantity: (
      state,
      action: PayloadAction<{ id: string; quantity: number }>
    ) => {
      const item = state.items.find((item) => item.id === action.payload.id);
      if (item) {
        item.quantity = Math.max(1, action.payload.quantity);
        saveCartToStorage(state.items);
      }
    },
    clearCart: (state) => {
      state.items = [];
      clearCartFromStorage();
    },
    getAllItems: (state) => {
      // This is a read-only action, but we can refresh from storage
      state.items = loadCartFromStorage();
    },
    removeItemByIndex: (state, action: PayloadAction<number>) => {
      state.items.splice(action.payload, 1);
      saveCartToStorage(state.items);
    },
  },
});

export const {
  addItem: addItemToLocalCart,
  removeItemById: removeItemFromLocalCart,
  updateItemQuantity: updateItemQuantityInLocalCart,
  clearCart: clearCartInLocalCart,
  getAllItems: getAllItemsInLocalCart,
  removeItemByIndex: removeItemByIndexInLocalCart,
} = localCartSlice.actions;

export const localCartReducer = localCartSlice.reducer;
